import { useState, useEffect } from "react";
import axios from "axios";
import { Search, Phone, MapPin, Clock, User, Trash2, Pencil, X, Save } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerPage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);

  // Edit customer modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editCustomer, setEditCustomer] = useState({
    first_name: "",
    last_name: "",
    address: "",
  });
  const [savingEdit, setSavingEdit] = useState(false);

  const [newCustomer, setNewCustomer] = useState({
    phone: "",
    first_name: "",
    last_name: "",
    address: ""
  });

  // Debounced search
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchQuery && searchQuery.length >= 2) {
        searchCustomers();
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  const searchCustomers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/customers/search?q=${searchQuery}`);
      setSearchResults(response.data.customers || []);
    } catch (error) {
      console.error("Müşteri arama hatası:", error);
    }
    setLoading(false);
  };

  const selectCustomer = async (customer) => {
    setSelectedCustomer(customer);
    setSearchQuery("");
    setSearchResults([]);
    
    // Load customer orders (last 2 only)
    try {
      const response = await axios.get(`${API}/orders?customer_id=${customer.id}`);
      // Sort by date and get last 2
      const sortedOrders = response.data.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setOrders(sortedOrders.slice(0, 2));
    } catch (error) {
      console.error("Siparişler yüklenirken hata:", error);
    }
  };

  // Tarih farkını hesapla
  const getTimeDifference = (dateString) => {
    const orderDate = new Date(dateString);
    const now = new Date();
    const diffMs = now - orderDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) {
      return `${diffDays} gün önce`;
    } else if (diffHours > 0) {
      return `${diffHours} saat önce`;
    } else if (diffMins > 0) {
      return `${diffMins} dakika önce`;
    } else {
      return 'Az önce';
    }
  };

  // Geçen süreyi hesapla (dakika ve saat)
  const getElapsedTime = (dateString) => {
    const orderDate = new Date(dateString);
    const now = new Date();
    const diffMs = now - orderDate;
    const diffMins = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    
    if (hours > 0) {
      return `${hours}s ${mins}dk`;
    } else {
      return `${mins}dk`;
    }
  };

  const openEditCustomer = () => {
    if (!selectedCustomer) return;
    setEditCustomer({
      first_name: selectedCustomer.first_name || "",
      last_name: selectedCustomer.last_name || "",
      address: selectedCustomer.address || "",
    });
    setShowEditModal(true);
  };

  const saveCustomerEdit = async () => {
    if (!selectedCustomer) return;
    if (!editCustomer.first_name?.trim() || !editCustomer.last_name?.trim()) {
      alert("Lütfen ad ve soyadı girin");
      return;
    }

    setSavingEdit(true);
    try {
      await axios.put(`${API}/customers/${selectedCustomer.id}`, {
        first_name: editCustomer.first_name.trim(),
        last_name: editCustomer.last_name.trim(),
        address: editCustomer.address?.trim() || "",
      });

      // Single source of truth: reload customer from backend
      const refreshed = await axios.get(`${API}/customers/${selectedCustomer.id}`);
      setSelectedCustomer(refreshed.data);

      setShowEditModal(false);
    } catch (error) {
      console.error("Müşteri güncelleme hatası:", error);
      alert("Müşteri güncellenemedi: " + (error.response?.data?.detail || error.message));
    } finally {
      setSavingEdit(false);
    }
  };

  const addCustomer = async () => {
    if (!newCustomer.phone || !newCustomer.first_name || !newCustomer.last_name) {
      return;
    }

    try {
      const response = await axios.post(`${API}/customers`, newCustomer);
      setShowAddModal(false);
      setNewCustomer({ phone: "", first_name: "", last_name: "", address: "" });
      selectCustomer(response.data);
    } catch (error) {
      console.error("Müşteri ekleme hatası:", error);
      if (error.response?.status === 400) {
        alert("Bu telefon numarası zaten kayıtlı!");
      }
    }
  };

  const deleteCustomer = async () => {
    if (!selectedCustomer) return;
    
    const confirm = window.confirm(
      `${selectedCustomer.first_name} ${selectedCustomer.last_name} isimli müşteriyi silmek istediğinize emin misiniz?`
    );
    
    if (!confirm) return;

    try {
      await axios.delete(`${API}/customers/${selectedCustomer.id}`);
      setSelectedCustomer(null);
      setOrders([]);
    } catch (error) {
      console.error("Müşteri silme hatası:", error);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('tr-TR');
  };

  return (
    <div className="p-8" data-testid="customer-page">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-800">Müşteri Sorgulama</h1>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
        >
          <User className="w-5 h-5" />
          <span>Yeni Müşteri Ekle</span>
        </button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8 relative">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <div className="flex items-center space-x-2">
              <Phone className="text-gray-400" size={24} />
              <input
                type="text"
                placeholder="Telefon, isim veya soyad ile arayın... (En az 2 karakter)"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 px-4 py-3 border-2 rounded-lg text-lg"
                data-testid="search-query-input"
              />
              {loading && (
                <div className="absolute right-4 top-1/2 -translate-y-1/2">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-600"></div>
                </div>
              )}
            </div>

            {/* Search Results Dropdown */}
            {searchResults.length > 0 && (
              <div className="absolute z-50 w-full mt-2 bg-white border-2 border-gray-200 rounded-lg shadow-lg max-h-96 overflow-auto">
                {searchResults.map((customer) => (
                  <div
                    key={customer.id}
                    onClick={() => selectCustomer(customer)}
                    className="p-4 hover:bg-gray-50 cursor-pointer border-b last:border-b-0 transition"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold text-lg text-gray-800">
                          {customer.first_name} {customer.last_name}
                        </p>
                        <p className="text-sm text-gray-600 mt-1">
                          📞 {customer.phone}
                        </p>
                        {customer.address && (
                          <p className="text-xs text-gray-500 mt-1">
                            📍 {customer.address}
                          </p>
                        )}
                      </div>
                      <button className="text-orange-600 hover:text-orange-800 font-medium text-sm">
                        Seç →
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {searchQuery.length >= 2 && searchResults.length === 0 && !loading && (
              <div className="absolute z-50 w-full mt-2 bg-white border-2 border-gray-200 rounded-lg shadow-lg p-4">
                <p className="text-gray-500 text-center">Sonuç bulunamadı</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Customer Info */}
      {selectedCustomer && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-gray-800">Müşteri Bilgileri</h2>
            <div className="flex items-center gap-3">
              <button
                onClick={openEditCustomer}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                <Pencil className="w-4 h-4" />
                <span>Düzenle</span>
              </button>
              <button
                onClick={deleteCustomer}
                className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
              >
                <Trash2 className="w-4 h-4" />
                <span>Müşteriyi Sil</span>
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <Phone className="text-orange-600" size={24} />
              </div>
              <div>
                <p className="text-sm text-gray-600">Telefon</p>
                <p className="text-lg font-semibold">{selectedCustomer.phone}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Phone className="text-blue-600" size={24} />
              </div>
              <div>
                <p className="text-sm text-gray-600">Ad Soyad</p>
                <p className="text-lg font-semibold">{selectedCustomer.first_name} {selectedCustomer.last_name}</p>
              </div>
            </div>
            {selectedCustomer.address && (
              <div className="flex items-center space-x-3 md:col-span-2">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <MapPin className="text-green-600" size={24} />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-600">Adres</p>
                  <p className="text-lg font-semibold">{selectedCustomer.address}</p>
                </div>
              </div>
            )}
            
            {/* Son Sipariş Bilgisi */}
            {orders.length > 0 && orders[0].payment_status === "ödenmedi" && (
              <div className="flex items-center space-x-3 md:col-span-2">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <Clock className="text-red-600" size={24} />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-600">Aktif Sipariş</p>
                  <p className="text-lg font-bold text-red-600">
                    ⏱️ Geçen: {getElapsedTime(orders[0].created_at)}
                  </p>
                </div>
              </div>
            )}
            
            {orders.length > 0 && (
              <div className="flex items-center space-x-3 md:col-span-2">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Clock className="text-blue-600" size={24} />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-600">Son Sipariş</p>
                  <p className="text-lg font-bold text-blue-600">
                    🕒 {getTimeDifference(orders[0].created_at)}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Order History */}
      {orders.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Son 2 Sipariş</h2>
          <div className="space-y-4">
            {orders.map(order => (
              <div key={order.id} className="border-2 border-gray-200 rounded-lg p-4 hover:border-orange-300 transition">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="text-sm text-gray-600 flex items-center">
                      <Clock size={16} className="mr-1" />
                      {formatDate(order.created_at)}
                    </p>
                    {/* Geçen Süre - Ödenmemiş siparişler için */}
                    {order.payment_status === "ödenmedi" && (
                      <p className="text-sm font-bold text-red-600 mt-1">
                        ⏱️ Geçen: {getElapsedTime(order.created_at)}
                      </p>
                    )}
                    <p className="text-lg font-semibold text-gray-800 mt-1">
                      {order.order_type.toUpperCase()}
                      {order.table_number && ` - ${order.table_number}`}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-orange-600">{order.total.toFixed(2)} ₺</p>
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                      order.payment_status === "ödendi" 
                        ? "bg-green-100 text-green-800"
                        : "bg-yellow-100 text-yellow-800"
                    }`}>
                      {order.payment_status}
                    </span>
                  </div>
                </div>
                <div className="bg-gray-50 rounded p-3">
                  <h4 className="font-semibold text-gray-700 mb-2 text-sm">Ürünler:</h4>
                  {order.items.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm py-1">
                      <span>{item.quantity}x {item.product_name}</span>
                      <span className="font-medium">{(item.price * item.quantity).toFixed(2)} ₺</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {selectedCustomer && orders.length === 0 && (
        <div className="text-center text-gray-400 py-12">
          <Clock size={64} className="mx-auto mb-4 opacity-50" />
          <p>Bu müşterinin henüz siparişi yok</p>
        </div>
      )}

      {/* Add Customer Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 w-[500px]">
            <h2 className="text-2xl font-bold mb-6">Yeni Müşteri Ekle</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Telefon Numarası *
                </label>
                <input
                  type="tel"
                  placeholder="0555 123 4567"
                  value={newCustomer.phone}
                  onChange={(e) => setNewCustomer({...newCustomer, phone: e.target.value})}
                  className="w-full px-4 py-2 border-2 rounded-lg focus:border-orange-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ad *
                </label>
                <input
                  type="text"
                  placeholder="Ahmet"
                  value={newCustomer.first_name}
                  onChange={(e) => setNewCustomer({...newCustomer, first_name: e.target.value})}
                  className="w-full px-4 py-2 border-2 rounded-lg focus:border-orange-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Soyad *
                </label>
                <input
                  type="text"
                  placeholder="Yılmaz"
                  value={newCustomer.last_name}
                  onChange={(e) => setNewCustomer({...newCustomer, last_name: e.target.value})}
                  className="w-full px-4 py-2 border-2 rounded-lg focus:border-orange-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Adres (Opsiyonel)
                </label>
                <textarea
                  placeholder="Atatürk Cad. No:5 Merkez/İstanbul"
                  value={newCustomer.address}
                  onChange={(e) => setNewCustomer({...newCustomer, address: e.target.value})}
                  className="w-full px-4 py-2 border-2 rounded-lg h-24 focus:border-orange-500 outline-none"
                />
              </div>
              <div className="flex space-x-4 pt-4">
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setNewCustomer({ phone: "", first_name: "", last_name: "", address: "" });
                  }}
                  className="flex-1 py-3 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 font-medium"
                >
                  İptal
                </button>
                <button
                  onClick={addCustomer}
                  disabled={!newCustomer.phone || !newCustomer.first_name || !newCustomer.last_name}
                  className="flex-1 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-bold disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Müşteri Ekle
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Edit Customer Modal */}
      {showEditModal && selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 w-[520px]">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Müşteri Düzenle</h2>
              <button
                onClick={() => !savingEdit && setShowEditModal(false)}
                className="p-2 rounded-lg hover:bg-gray-100"
                aria-label="Kapat"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="p-4 rounded-lg border border-gray-200 bg-gray-50">
                <p className="text-xs text-gray-500">Telefon (değiştirilemez)</p>
                <p className="text-lg font-semibold text-gray-800">{selectedCustomer.phone}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Ad *</label>
                  <input
                    type="text"
                    value={editCustomer.first_name}
                    onChange={(e) => setEditCustomer({ ...editCustomer, first_name: e.target.value })}
                    className="w-full px-4 py-2 border-2 rounded-lg focus:border-blue-500 outline-none"
                    disabled={savingEdit}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Soyad *</label>
                  <input
                    type="text"
                    value={editCustomer.last_name}
                    onChange={(e) => setEditCustomer({ ...editCustomer, last_name: e.target.value })}
                    className="w-full px-4 py-2 border-2 rounded-lg focus:border-blue-500 outline-none"
                    disabled={savingEdit}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Adres</label>
                <textarea
                  value={editCustomer.address}
                  onChange={(e) => setEditCustomer({ ...editCustomer, address: e.target.value })}
                  className="w-full px-4 py-2 border-2 rounded-lg h-28 focus:border-blue-500 outline-none"
                  disabled={savingEdit}
                  placeholder="Adres girin..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowEditModal(false)}
                  disabled={savingEdit}
                  className="flex-1 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-medium disabled:bg-gray-200 disabled:cursor-not-allowed"
                >
                  <span className="inline-flex items-center justify-center gap-2">
                    <X className="w-4 h-4" />
                    İptal
                  </span>
                </button>
                <button
                  onClick={saveCustomerEdit}
                  disabled={savingEdit || !editCustomer.first_name?.trim() || !editCustomer.last_name?.trim()}
                  className="flex-1 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-bold disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  <span className="inline-flex items-center justify-center gap-2">
                    <Save className="w-4 h-4" />
                    {savingEdit ? "Kaydediliyor..." : "Kaydet"}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default CustomerPage;